import { inject, Injectable, InjectionToken } from '@angular/core';
import { isDefined } from '@sama/js-core';
import { environment } from '../../../environments/environment';
import { TaskWorkspaceStore } from '../../core/h3ml/state/task-workspace-store';
import { FeatureFlagService } from '../../core/shared/misc-services/feature-flag.service';
import {
  Tracker,
  TrackingEvent,
} from '../../cross-team-shared/event-tracking/event-utils';
import { RollbarService } from '../../cross-team-shared/messaging/rollbar.service';
import { EventTrackingOptions } from '../../team-apis/core/event-tracking';
import { ConsoleTracker } from './console-tracker';
import { SegmentTracker } from './segment-tracker';

export const rootTrackerToken = new InjectionToken('RootTracker', {
  providedIn: 'root',
  factory: () => {
    if (environment.shouldUseSegmentTracker && (window as any).analytics) {
      return new SegmentTracker(
        'sama_hub',
        // the segment client swaps out this `analytics` global variable after
        // it loads, so don't extract it as a constant here
        () => (window as any).analytics,
      );
    } else {
      return new ConsoleTracker();
    }
  },
});

// interactive events (events that are the result of user actions) that we do
// not want to include in the total duration of the task
const eventIdsNotAffectingDuration = new Set(['stop_work', 'submit_task']);

@Injectable()
export class RootTrackerWrapper implements Tracker {
  // public so it can be stubbed for tests
  rootTracker = inject(rootTrackerToken);
  featureFlagService = inject(FeatureFlagService);

  constructor(
    private taskWorkspaceStore: TaskWorkspaceStore,
    private rollbarService: RollbarService,
  ) {
    if (
      environment.shouldUseSegmentTracker &&
      this.rootTracker.constructor.name !== SegmentTracker.name
    ) {
      this.rollbarService.critical(
        'Event tracker not sending events to server',
      );
    }
  }

  track(event: TrackingEvent, options: EventTrackingOptions = {}) {
    options.isInteractive ??= true;
    this.addSplitViewContext(event);

    this.rootTracker.track(event, options);

    if (options.isInteractive && !eventIdsNotAffectingDuration.has(event.id)) {
      this.taskWorkspaceStore('lastInteractiveEventTimestamp').set(
        performance.now(),
      );
    }
  }

  private addSplitViewContext(event: TrackingEvent) {
    event.mode = event.mode ? [event.mode] : [];
    const isInSplitViewMode = isDefined(
      this.taskWorkspaceStore.state().splitViewOutputName,
    );
    if (isInSplitViewMode) {
      event.mode.push('split_view');
    }
  }
}
